import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Search, SlidersHorizontal, Columns3, Eye, EyeOff, Loader2, Table2, Plus, X, ArrowRightLeft, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const GENDERS = ["Male", "Female", "Other"];
const CATEGORIES = ["General", "OBC", "SC", "ST", "Other"];
const TC = ["Not Applicable", "Pending", "Received", "Submitted", "Issued"];
const STATUS = ["Active", "Inactive"];
const BLOOD = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const BULK_SET_FIELDS = [
  { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
  { key: "category", label: "Category", type: "select", options: CATEGORIES },
  { key: "gender", label: "Gender", type: "select", options: GENDERS },
  { key: "blood_group", label: "Blood Group", type: "select", options: BLOOD },
  { key: "tc_status", label: "TC Status", type: "select", options: TC },
  { key: "house", label: "House", type: "text" },
  { key: "religion", label: "Religion", type: "text" },
  { key: "caste", label: "Caste", type: "text" },
  { key: "stream", label: "Stream", type: "text" },
  { key: "academic_session", label: "Academic Session", type: "text" },
];

const FIELDS = [
  { key: "admission_number", label: "Adm No", w: 110 },
  { key: "name", label: "Name", w: 170 },
  { key: "class_id", label: "Class", type: "class", w: 130 },
  { key: "section", label: "Section", type: "section", w: 90 },
  { key: "roll_number", label: "Roll No", w: 90 },
  { key: "father_name", label: "Father", w: 150 },
  { key: "mother_name", label: "Mother", w: 150 },
  { key: "phone", label: "Phone", w: 130 },
  { key: "alt_phone", label: "Alt Phone", w: 130 },
  { key: "dob", label: "DOB", type: "date", w: 150 },
  { key: "gender", label: "Gender", type: "select", options: GENDERS, w: 110 },
  { key: "blood_group", label: "Blood", type: "select", options: BLOOD, w: 90 },
  { key: "height", label: "Height (cm)", type: "number", w: 100 },
  { key: "weight", label: "Weight (kg)", type: "number", w: 100 },
  { key: "category", label: "Category", type: "select", options: CATEGORIES, w: 120 },
  { key: "house", label: "House", type: "select", w: 110 },
  { key: "caste", label: "Caste", w: 110 },
  { key: "religion", label: "Religion", w: 110 },
  { key: "aadhar_number", label: "Aadhar", w: 140 },
  { key: "apaar_id", label: "Apaar ID", w: 130 },
  { key: "admission_date", label: "Adm Date", type: "date", w: 150 },
  { key: "address", label: "Address", w: 180 },
  { key: "city", label: "City", w: 110 },
  { key: "state", label: "State", w: 110 },
  { key: "pin_code", label: "PIN", w: 90 },
  { key: "tc_status", label: "TC Status", type: "select", options: TC, w: 140 },
  { key: "status", label: "Status", type: "select", options: STATUS, w: 110 },
];

const DEFAULT_HIDDEN = ["caste", "religion", "aadhar_number", "apaar_id", "admission_date", "address", "city", "state", "pin_code"];

const FILTER_FIELDS = [
  { key: "class_name", label: "Class", type: "class" },
  { key: "section", label: "Section", type: "section" },
  { key: "gender", label: "Gender", type: "select", options: GENDERS },
  { key: "category", label: "Category", type: "select", options: CATEGORIES },
  { key: "house", label: "House", type: "text" },
  { key: "admission_type", label: "Admission Type", type: "select", options: ["New", "Old", "Left"] },
  { key: "status", label: "Status", type: "select", options: STATUS },
  { key: "tag", label: "Tag", type: "select", options: [] },
];

const SORT_OPTIONS = [
  { value: "name", label: "Name" },
  { value: "admission_number", label: "Admission No" },
  { value: "roll_number", label: "Roll No" },
  { value: "section", label: "Section" },
  { value: "father_name", label: "Father Name" },
  { value: "created_at", label: "Date Added" },
];

function EditCell({ id, field, value, type, classes, classOptions, options, sectionOptions, flashKey, onSave }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  const flashed = flashKey === `${id}-${field.key}`;
  const base = `w-full h-8 px-2 rounded border text-sm bg-white outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 ${flashed ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200"}`;
  const testid = `cell-${field.key}-${id}`;

  if (type === "class") {
    const opts = (classOptions && classOptions.length) ? classOptions : classes.map((c) => ({ value: c.id, label: c.name }));
    return (
      <select value={v} onChange={(e) => { setV(e.target.value); onSave(id, field.key, e.target.value); }} className={base} data-testid={testid}>
        <option value=""></option>
        {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (type === "section") {
    const opts = [...new Set([...(sectionOptions || []), ...(v ? [v] : [])])];
    return (
      <select value={v} onChange={(e) => { setV(e.target.value); onSave(id, field.key, e.target.value); }} className={base} data-testid={testid}>
        <option value=""></option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (type === "select") {
    const opts = [...new Set([...((options && options.length ? options : field.options) || []), ...(v ? [v] : [])])];
    return (
      <select value={v} onChange={(e) => { setV(e.target.value); onSave(id, field.key, e.target.value); }} className={base} data-testid={testid}>
        <option value=""></option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <input type={type || "text"} value={v} onChange={(e) => setV(e.target.value)}
      onBlur={() => { if ((v ?? "") !== (value ?? "")) onSave(id, field.key, v); }}
      className={base} data-testid={testid} />
  );
}

export default function BulkEdit() {
  const [data, setData] = useState({ students: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [classes, setClasses] = useState([]);
  const [tags, setTags] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [hidden, setHidden] = useState(DEFAULT_HIDDEN);
  const [showFilter, setShowFilter] = useState(false);
  const [applied, setApplied] = useState({});
  const [flashKey, setFlashKey] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [moveClass, setMoveClass] = useState("");
  const [moveSection, setMoveSection] = useState("");
  const [moving, setMoving] = useState(false);
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkSetting, setBulkSetting] = useState(false);
  const [bulkPanel, setBulkPanel] = useState(null);
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const [allowedFields, setAllowedFields] = useState(null);

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/settings/tags").then((r) => setTags(r.data.tags || [])).catch(() => {});
    api.get("/sections").then((r) => setAllSections(r.data.sections || [])).catch(() => {});
    if (user?.role === "staff") api.get("/settings/staff-bulk-fields").then((r) => setAllowedFields(r.data.fields || [])).catch(() => setAllowedFields([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setSelected(new Set());
    const params = { page, limit };
    if (search) params.search = search;
    Object.entries(applied).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
    api.get("/students", { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [search, applied, page, limit]);
  useEffect(() => { load(); }, [load]);

  const save = async (id, field, value) => {
    try {
      const { data: res } = await api.patch(`/students/${id}/field`, { field, value });
      const upd = (res && res.updates) || { [field]: value };
      setData((p) => ({ ...p, students: p.students.map((s) => s.id === id ? { ...s, ...upd } : s) }));
      setFlashKey(`${id}-${field}`);
      setTimeout(() => setFlashKey(null), 900);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const toggleRow = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((p) => (p.size && data.students.every((s) => p.has(s.id))) ? new Set() : new Set(data.students.map((s) => s.id)));
  const doMove = async () => {
    if (!moveClass) { toast.error("Choose a class to move to"); return; }
    setMoving(true);
    try {
      const { data: res } = await api.post("/students/bulk-move", { student_ids: [...selected], class_name: moveClass, section: moveSection });
      toast.success(`Moved ${res.moved} student(s) to ${moveClass}${moveSection ? " - " + moveSection : ""}`);
      setSelected(new Set()); setMoveClass(""); setMoveSection(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setMoving(false); }
  };

  const doBulkSet = async () => {
    if (!bulkField) { toast.error("Choose a field to set"); return; }
    setBulkSetting(true);
    try {
      const { data: res } = await api.post("/students/bulk-set-field", { student_ids: [...selected], field: bulkField, value: bulkValue });
      const lbl = BULK_SET_FIELDS.find((f) => f.key === bulkField)?.label || bulkField;
      toast.success(`Updated ${lbl} for ${res.updated} student(s)`);
      setSelected(new Set()); setBulkField(""); setBulkValue(""); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setBulkSetting(false); }
  };

  const cols = FIELDS.filter((f) => !hidden.includes(f.key));
  const canEditField = (k) => !isStaff || (allowedFields || []).includes(k);
  const canMove = !isStaff || (allowedFields || []).includes("class_id") || (allowedFields || []).includes("section");
  const bulkSetFields = isStaff ? BULK_SET_FIELDS.filter((f) => (allowedFields || []).includes(f.key)) : BULK_SET_FIELDS;
  const toggleCol = (k) => setHidden(hidden.includes(k) ? hidden.filter((x) => x !== k) : [...hidden, k]);

  const sectionsByName = {};
  classes.forEach((c) => { const n = c.name; if (!sectionsByName[n]) sectionsByName[n] = []; const sec = (c.section || "").trim(); if (sec && !sectionsByName[n].includes(sec)) sectionsByName[n].push(sec); });
  const classNameById = Object.fromEntries(classes.map((c) => [c.id, c.name]));
  const sectionsForClass = (classId) => { const n = classNameById[classId]; const fromC = n ? (sectionsByName[n] || []) : []; return fromC.length ? fromC : allSections; };
  const classById = Object.fromEntries(classes.map((c) => [c.id, c]));
  const repByName = {}; classes.forEach((c) => { if (!(c.name in repByName)) repByName[c.name] = c.id; });
  const classOptions = Object.keys(repByName).map((name) => ({ value: repByName[name], label: name }));
  const repIdForClass = (cid) => { const c = classById[cid]; return c ? repByName[c.name] : (cid || ""); };
  const classNames = classOptions.map((o) => o.label);
  const moveSections = (moveClass && (sectionsByName[moveClass] || []).length) ? sectionsByName[moveClass] : allSections;
  const allChecked = data.students.length > 0 && data.students.every((s) => selected.has(s.id));
  const bf = BULK_SET_FIELDS.find((f) => f.key === bulkField);
  const distinctVals = (key) => Array.from(new Set(data.students.map((s) => (s[key] == null ? "" : String(s[key])).trim()).filter(Boolean)));
  const optionsFor = (field) => Array.from(new Set([...(field.options || []), ...distinctVals(field.key)]));
  const classNamesList = Array.from(new Set(classes.map((c) => c.name).filter(Boolean)));
  const sectionsForName = (name) => { const s = name ? (sectionsByName[name] || []) : []; return s.length ? s : allSections; };

  const setFilter = (key, value) => {
    setApplied((prev) => {
      const next = { ...prev };
      if (value === "" || value == null || value === "__any__") { delete next[key]; if (key === "class_name") delete next.section; }
      else next[key] = value;
      return next;
    });
    setPage(1);
  };
  const clearFilters = () => { setApplied({}); setPage(1); };

  const totalPages = Math.ceil(data.total / limit) || 1;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Table2 className="text-indigo-500" size={26} />Bulk Edit</h1>
        <p className="text-slate-500">Edit inline — changes save automatically when you leave a cell or press Tab. No save button needed.</p>
        {isStaff && <p className="text-xs text-amber-600 mt-1" data-testid="be-staff-note">You can edit only the fields enabled by your admin, for students in your assigned classes.</p>}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={showFilter || Object.keys(applied).length ? "default" : "outline"} size="sm" className="gap-1.5" onClick={() => setShowFilter(!showFilter)} data-testid="be-filter-btn"><SlidersHorizontal size={15} />Advanced Filter</Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="be-columns-btn"><Columns3 size={15} />Columns</Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-60 p-2 max-h-80 overflow-auto" data-testid="be-columns-panel">
              <div className="text-xs font-semibold text-slate-500 uppercase px-2 py-1">Show Columns</div>
              {FIELDS.map((f) => (
                <button key={f.key} onClick={() => toggleCol(f.key)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 text-sm text-left" data-testid={`be-col-toggle-${f.key}`}>
                  {hidden.includes(f.key) ? <EyeOff size={15} className="text-slate-300" /> : <Eye size={15} className="text-indigo-600" />}
                  <span className={hidden.includes(f.key) ? "text-slate-400" : "text-slate-700"}>{f.label}</span>
                </button>
              ))}
            </PopoverContent>
          </Popover>
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9 h-9" placeholder="Search name, adm no, father, phone…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="be-search" />
          </div>
          {canMove && <Button variant={bulkPanel === "move" ? "default" : "outline"} size="sm" className="gap-1.5" onClick={() => setBulkPanel(bulkPanel === "move" ? null : "move")} data-testid="toolbar-bulk-move-btn"><ArrowRightLeft size={15} />Bulk Move</Button>}
          <Button variant={bulkPanel === "set" ? "default" : "outline"} size="sm" className="gap-1.5" onClick={() => setBulkPanel(bulkPanel === "set" ? null : "set")} data-testid="toolbar-set-field-btn"><ListChecks size={15} />Set Field</Button>
          <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
            <SelectTrigger className="w-24 h-9" data-testid="be-page-size"><SelectValue /></SelectTrigger>
            <SelectContent>{[25, 50, 100, 200].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {showFilter && (
          <div className="border-t border-slate-100 pt-3" data-testid="be-filter-panel">
            <div className="flex flex-wrap items-end gap-2.5">
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-0.5">Sort By</label>
                <Select value={applied.sort_by || "__any__"} onValueChange={(v) => setFilter("sort_by", v)}>
                  <SelectTrigger className="w-36 h-9" data-testid="be-filter-sort-by"><SelectValue placeholder="Default" /></SelectTrigger>
                  <SelectContent><SelectItem value="__any__">Default</SelectItem>{SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-0.5">Order</label>
                <Select value={applied.sort_dir || "asc"} onValueChange={(v) => setFilter("sort_dir", v)}>
                  <SelectTrigger className="w-28 h-9" data-testid="be-filter-sort-dir"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="asc">A → Z</SelectItem><SelectItem value="desc">Z → A</SelectItem></SelectContent>
                </Select>
              </div>
              {FILTER_FIELDS.map((field) => {
                const val = applied[field.key] ?? "";
                return (
                  <div key={field.key} className="flex flex-col gap-1" data-testid={`be-filter-chip-${field.key}`}>
                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide px-0.5">{field.label}</label>
                    {field.type === "class" ? (
                      <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                        <SelectTrigger className="w-40 h-9" data-testid="be-filter-class-select"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent><SelectItem value="__any__">Any</SelectItem>{classNamesList.map((nm) => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : field.type === "section" ? (
                      <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                        <SelectTrigger className="w-32 h-9" data-testid="be-filter-section-select"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent><SelectItem value="__any__">Any</SelectItem>{sectionsForName(applied.class_name).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : field.type === "select" ? (
                      <Select value={val || "__any__"} onValueChange={(v) => setFilter(field.key, v)}>
                        <SelectTrigger className="w-36 h-9" data-testid={`be-filter-${field.key}-select`}><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent><SelectItem value="__any__">Any</SelectItem>{(field.key === "tag" ? tags : field.options).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <Input className="w-36 h-9" value={val} onChange={(e) => setFilter(field.key, e.target.value)} placeholder="Any" data-testid={`be-filter-${field.key}-input`} />
                    )}
                  </div>
                );
              })}
              {Object.keys(applied).length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="be-clear-filter" className="h-9 text-rose-500"><X size={14} className="mr-1" />Clear All</Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowFilter(false)} data-testid="be-hide-filter" className="h-9"><X size={14} className="mr-1" />Hide Filters</Button>
            </div>
          </div>
        )}
      </div>

      {bulkPanel && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 space-y-2" data-testid="bulk-action-bar">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-indigo-700" data-testid="bulk-selected-count">{selected.size} selected</span>
            {selected.size === 0 && <span className="text-sm text-amber-600">— tick students below to apply</span>}
            {selected.size > 0 && <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="move-clear-btn">Clear selection</Button>}
            <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto text-slate-500" onClick={() => setBulkPanel(null)} data-testid="bulk-panel-close"><X size={15} /></Button>
          </div>
          {bulkPanel === "move" && (
            <div className="flex flex-wrap items-center gap-2 border-t border-indigo-200/70 pt-2">
              <span className="text-sm text-slate-500">Move to</span>
              <Select value={moveClass} onValueChange={(v) => { setMoveClass(v); setMoveSection(""); }}>
                <SelectTrigger className="w-40 h-9" data-testid="move-class-select"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>{classNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={moveSection} onValueChange={setMoveSection}>
                <SelectTrigger className="w-32 h-9" data-testid="move-section-select"><SelectValue placeholder="Section" /></SelectTrigger>
                <SelectContent>{moveSections.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" onClick={doMove} disabled={moving || !moveClass || selected.size === 0} data-testid="move-apply-btn">{moving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ArrowRightLeft size={14} className="mr-1" />}Move {selected.size || ""}</Button>
            </div>
          )}
          {bulkPanel === "set" && (
            <div className="flex flex-wrap items-center gap-2 border-t border-indigo-200/70 pt-2">
              <span className="text-sm text-slate-500">Set field</span>
              <Select value={bulkField} onValueChange={(v) => { setBulkField(v); setBulkValue(""); }}>
                <SelectTrigger className="w-44 h-9" data-testid="bulkset-field-select"><SelectValue placeholder="Choose field" /></SelectTrigger>
                <SelectContent>{bulkSetFields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-sm text-slate-500">to</span>
              {bf?.type === "select" ? (
                <Select value={bulkValue} onValueChange={setBulkValue}>
                  <SelectTrigger className="w-44 h-9" data-testid="bulkset-value-select"><SelectValue placeholder="Value" /></SelectTrigger>
                  <SelectContent>{bf.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input className="w-44 h-9" placeholder="Value" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} disabled={!bulkField} data-testid="bulkset-value-input" />
              )}
              <Button size="sm" variant="secondary" onClick={doBulkSet} disabled={bulkSetting || !bulkField || selected.size === 0} data-testid="bulkset-apply-btn">{bulkSetting ? <Loader2 size={14} className="mr-1 animate-spin" /> : null}Apply to {selected.size || ""}</Button>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> :
          data.students.length === 0 ? (
            <div className="py-16 text-center text-slate-400"><Table2 size={40} className="mx-auto mb-2 opacity-40" />No students found.</div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <table className="text-sm border-collapse">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 border border-slate-200 bg-slate-50 sticky left-0 z-20 text-center"><input type="checkbox" checked={allChecked} onChange={toggleAll} className="w-4 h-4 accent-indigo-600" data-testid="be-select-all" /></th>
                    <th className="px-2 py-2 border border-slate-200 bg-slate-50 text-left">#</th>
                    {cols.map((c) => <th key={c.key} className="px-2 py-2 border border-slate-200 bg-slate-50 text-left whitespace-nowrap" style={{ minWidth: c.w }}>{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((s, idx) => (
                    <tr key={s.id} data-testid={`be-row-${s.admission_number}`} className={selected.has(s.id) ? "bg-indigo-50/50" : ""}>
                      <td className="px-2 py-1.5 border border-slate-200 bg-white sticky left-0 z-10 text-center"><input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleRow(s.id)} className="w-4 h-4 accent-indigo-600" data-testid={`be-select-${s.admission_number}`} /></td>
                      <td className="px-2 py-1.5 border border-slate-200 bg-white text-slate-400 tabular">{(page - 1) * limit + idx + 1}</td>
                      {cols.map((c) => (
                        <td key={c.key} className="px-1 py-1 border border-slate-200" style={{ minWidth: c.w }}>
                          {canEditField(c.key)
                            ? <EditCell id={s.id} field={c} value={c.type === "class" ? repIdForClass(s.class_id) : s[c.key]} type={c.type} classes={classes} classOptions={classOptions} options={c.type === "select" ? optionsFor(c) : undefined} sectionOptions={c.type === "section" ? sectionsForClass(s.class_id) : undefined} flashKey={flashKey} onSave={save} />
                            : <span className="px-2 text-sm text-slate-500 block truncate" data-testid={`cell-ro-${c.key}-${s.id}`}>{c.type === "class" ? (classNameById[s.class_id] || "") : (s[c.key] ?? "")}</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {data.total > limit && (
        <div className="flex justify-between items-center">
          <span className="text-sm text-slate-500">Page {page} of {totalPages} · {data.total} total</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="be-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="be-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
